import { useMemo } from 'react';
import { Typography } from '@mui/material';
import { default as Grid } from '@mui/material/Unstable_Grid2';
import { useTheme } from '@mui/material/styles';

import { MetricBarChart, MetricCard, PageSection } from '@occc/ui-design-system';
import { Button } from '@occc-app/design-system';
import { RetailerNetworkLocationDowntimeMetricsOverview } from '@occc/graphql';

import { DisconnectedStoresTooltip } from './disconnected-stores-tooltip.component';
import { getBarColors } from './my-stores.constants';
import { MyStoresChartTooltip } from './my-stores-chart-tooltip';
import { getMetrics } from './my-stores.utils';
import { ConnectedStoresMetric } from './my-stores.types';

export type MyStoresProps = {
  data: RetailerNetworkLocationDowntimeMetricsOverview | undefined | null;
  isLoading?: boolean;
  hasError?: boolean;
  onViewMyStores: () => void;
};

export const MyStores = ({
  data,
  isLoading = false,
  hasError = false,
  onViewMyStores,
}: MyStoresProps) => {
  const theme = useTheme();
  const numberOfStores = data?.numberOfStores ?? 0;
  const numberOfConnectedStores = data?.numberOfConnectedStores ?? 0;
  const numberOfDisconnectedStores = numberOfStores - numberOfConnectedStores;

  const metrics: ConnectedStoresMetric[] = useMemo(
    () => getMetrics(data?.disconnectedStoresTimeSeriesOfLast30Days ?? [], numberOfStores),
    [data?.disconnectedStoresTimeSeriesOfLast30Days, numberOfStores],
  );
  const noDataMessage = useMemo(
    () =>
      `We're still gathering connection status for ${
        numberOfStores > 1 ? 'these locations' : 'this location'
      }. Check back soon.`,
    [numberOfStores],
  );

  const isEmpty = !metrics.length;

  const barColors = getBarColors(theme.palette);

  return (
    <PageSection
      title='My Stores Connectivity'
      description='Monitor the connectivity status of your stores over the past 30 days.'
      actions={
        <Button color='primary' variant='contained' onClick={onViewMyStores}>
          View All Stores
        </Button>
      }
      isLoading={isLoading}
      hasError={hasError}
    >
      {isEmpty ? (
        <Typography my={4} textAlign='center' variant='body2' color='text.secondary'>
          {noDataMessage}
        </Typography>
      ) : (
        <Grid container spacing={1} p={3} pb={2}>
          <Grid container xs={12} md={6}>
            <Grid xs={6}>
              <MetricCard
                isTrendHidden
                label='Connected Stores'
                value={numberOfConnectedStores}
                valueType='number'
                valueTypographyProps={{ variant: 'h5', fontWeight: 500 }}
              />
            </Grid>
            <Grid xs={6}>
              <MetricCard
                isTrendHidden
                label='Disconnected Stores'
                tooltip={<DisconnectedStoresTooltip />}
                value={numberOfDisconnectedStores}
                valueTypographyProps={{ variant: 'h5', fontWeight: 500 }}
              />
            </Grid>
            <Grid xs={6}>
              <MetricCard
                isTrendHidden
                label='Total Stores'
                value={numberOfStores}
                valueTypographyProps={{ variant: 'h5', fontWeight: 500 }}
              />
            </Grid>
          </Grid>

          <Grid xs={12} md={6}>
            <Typography variant='subtitle1' ml={2} gutterBottom>
              Disconnected Stores
            </Typography>
            <MetricBarChart
              metrics={metrics}
              barColor={barColors}
              noDataLabel={noDataMessage}
              barWidth={6}
              height={120}
              tickCount={5}
              isLegendVisible={false}
              isBarStacked
              tooltipContent={<MyStoresChartTooltip />}
            />
          </Grid>
        </Grid>
      )}
      {/* Revert this commit to restore the disconnected stores alert. Should be re-added as part of https://stuzo.atlassian.net/browse/OCST-21197 */}
      {/* {!isEmpty && numberOfDisconnectedStores > 0 && (
        <Alert
          sx={{ m: 3, mt: 0, cursor: 'pointer' }}
          severity='error'
          onClick={onViewMyStores}
          icon={<InfoOutlinedIcon fontSize='small' />}
        >
          There are currently {numberOfDisconnectedStores} disconnected stores. Tap this banner to
          view them
        </Alert>
      )} */}
    </PageSection>
  );
};
